import React, { FC, useCallback, useState } from 'react'
import { Box } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import { ReactComponent as ArrowLeftIcon } from 'assets/svg/ArrowLeftIcon.svg'
import IconButton from '@material-ui/core/IconButton'
import Button from 'UI/Button/ButtonWithPreloader'
import { generatePath, useHistory, useParams } from 'react-router-dom'
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress'
import { useMutation, useQuery, useQueryCache } from 'react-query'
import { deleteFileFromRep, getRepById, setManagementFee } from 'services/rep'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import { REP_EDIT_URL, TRANSACTIONS_URL, UPLOAD_USER_PAGE_DOCS, WITHDRAWAL_URL } from 'constants/routes'
import { ReactComponent as EditPenIcon } from 'assets/svg/EditPenIcon.svg'
import BusinessInfo from 'components/CompanyInfo/BusinessInfo'
import { convertEmptyField } from 'utils/formatters'
import { ReactComponent as UploadIcon } from 'assets/svg/UploadIcon.svg'
import { TableIsoDocuments, useIso } from 'hooks/useIso'
import ConfirmationContent from 'UI/Modal/ConfirmationContent'
import ModalComponent from 'UI/Modal'
import { SOMETHING_WENT_WRONG } from 'constants/errors'
import { getFile } from 'services/storage'
import { createLinkToDownLoad } from 'utils/downloadFile'
import { useSnackbar } from 'notistack'
import {
  CREATE_REP_PERM,
  REP_MODULE_PERM,
  SET_MANAGEMENT_FEE,
  TRANSACTION_PERM,
  UPLOAD_USER_PAGE_DOCS_PERM,
  WITHDRAWAL_PERM,
} from 'constants/permissions'
import { usePermission } from 'hooks/usePermission'
import UserPageInfo from 'components/UserPageInfo'
import SetManagementRepFeeForm from 'components/SetManagementRepFee'
import { useAdditionalMaterialStyle } from 'containers/MaterialUiContainer/additionalStyles'

const InfoRepContainer: FC = () => {
  const queryCache = useQueryCache()
  const aStyle = useAdditionalMaterialStyle()
  const { enqueueSnackbar } = useSnackbar()
  const { hasPermission, isOwnRep } = usePermission()
  const { goBack, push } = useHistory()
  const [openManagementFee, setOpenManagementFee] = useState(false)
  const [deleteFileState, setDeleteFileState] = useState({
    id: 0,
    open: false,
  })
  const { id } = useParams<{ id: string }>()
  const { data, isLoading } = useQuery(['repById', id], () => getRepById(id), {
    retry: false,
    cacheTime: 0,
  })

  const [removeDoc, { isLoading: isLoadingRemove }] = useMutation(deleteFileFromRep, {
    onSuccess: (_, variables) => {
      setDeleteFileState({
        open: false,
        id: 0,
      })
      queryCache.setQueryData(['repById', id], {
        ...data,
        data: {
          ...data?.data,
          documents: data?.data.documents.filter((d) => d.id !== variables),
        },
      })
      enqueueSnackbar('Successfully deleted document')
    },
    onError: () => {
      enqueueSnackbar(SOMETHING_WENT_WRONG)
    },
  })

  const [setManagementFeeMut, { isLoading: isLoadingSetManagementFee }] = useMutation(setManagementFee, {
    onSuccess: (_, variables) => {
      setOpenManagementFee(false)
      enqueueSnackbar('Management Fee changed')
      queryCache.setQueryData(['repById', id], {
        ...data,
        data: {
          ...data?.data,
          managementFee: variables.feeValue,
        },
      })
    },
    onError: () => {
      enqueueSnackbar(SOMETHING_WENT_WRONG)
    },
  })

  const handleDownLoadFile = useCallback((url, name) => {
    getFile(url).then((res) => {
      createLinkToDownLoad(res.data, name)
    })
  }, [])

  const { columnsIsoDocuments } = useIso({
    handleDeleteFile: (idFile) => {
      setDeleteFileState({
        id: idFile,
        open: true,
      })
    },
    canDelete: hasPermission(REP_MODULE_PERM),
    handleDownloadFile: handleDownLoadFile,
  })

  return (
    <Box className={aStyle.wrapper}>
      {isLoading || !data ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <>
          <Box display="flex" alignItems="center">
            <Box ml="3rem" mr="1.5rem">
              <IconButton color="inherit" onClick={() => goBack()}>
                <ArrowLeftIcon />
              </IconButton>
            </Box>
            <Typography color="textSecondary" variant="h1">
              {data.data.businessName}
            </Typography>
            <Box mr="3rem" flex="1" display="flex" alignItems="center" justifyContent="flex-end">
              {(hasPermission(TRANSACTION_PERM) || isOwnRep(data?.data.id)) && (
                <Button
                  onClick={() =>
                    push(
                      generatePath(TRANSACTIONS_URL, {
                        id: data.data.id,
                        type: 'rep',
                        businessName: data.data.businessName,
                      }),
                    )
                  }
                  variant="outlined"
                  color="secondary"
                >
                  Activity
                </Button>
              )}
              {(hasPermission(WITHDRAWAL_PERM) || isOwnRep(data?.data.id)) && (
                <Box ml="1rem">
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() =>
                      push(
                        generatePath(WITHDRAWAL_URL, {
                          id: data.data.id,
                          type: 'rep',
                          businessName: data.data.businessName,
                        }),
                      )
                    }
                  >
                    Withdrawals
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
          <Box mt="2rem">
            <Grid container spacing={4}>
              <Grid item xs={6}>
                <Paper elevation={0}>
                  <Box width="100%" display="flex" justifyContent="flex-end" p="1rem" position="relative">
                    {hasPermission(CREATE_REP_PERM) && (
                      <IconButton
                        className={aStyle.absoluteEditButton}
                        size="small"
                        onClick={() =>
                          push(
                            generatePath(REP_EDIT_URL, {
                              id: data.data.id.toString(),
                            }),
                          )
                        }
                      >
                        <EditPenIcon />
                      </IconButton>
                    )}
                  </Box>
                  <Box pb="2rem" px="3rem">
                    <Box mb="2rem" display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="h3" color="textSecondary">
                        General Information
                      </Typography>
                      {hasPermission(SET_MANAGEMENT_FEE) && (
                        <Button onClick={() => setOpenManagementFee(true)} variant="outlined" color="secondary">
                          Set Management Fee
                        </Button>
                      )}
                    </Box>
                    <Grid container spacing={2}>
                      <Grid
                        item
                        container
                        xs={6}
                        spacing={3}
                        style={{
                          alignContent: 'flex-start',
                        }}
                      >
                        <Grid item xs={6}>
                          <Typography>First Name</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography color="textSecondary">{data.data.firstName}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography>Last Name</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography color="textSecondary">{data.data.lastName}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography>Mobile</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography color="textSecondary">{convertEmptyField(data.data.mobile)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography>Phone</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography color="textSecondary">{data.data.phone}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography>Email</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography color="textSecondary">{data.data.email}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography>Fax</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography color="textSecondary">{convertEmptyField(data.data.fax)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography>Available Contracts</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Box>
                            {data.data.availableCompanies && data.data.availableCompanies.length > 0 ? (
                              data.data.availableCompanies.map((company) => (
                                <Typography key={company.id} display="block" color="textSecondary">
                                  {company.name}
                                </Typography>
                              ))
                            ) : (
                              <Typography color="textSecondary">All</Typography>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                      <Grid item container xs={6} spacing={3}>
                        <UserPageInfo userPage={data.data.userPage} nameOnCheck={data.data.nameOnCheck} />
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper elevation={0}>
                  <BusinessInfo
                    street={data.data.street}
                    businessName={data.data.businessName}
                    city={data.data.city}
                    state={data.data.state}
                    federalTaxId={data.data.federalTaxId}
                    zipCode={data.data.zipCode}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Box>
          <Box mt={5}>
            <Paper elevation={0}>
              <Box p="3rem" display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h3" color="textSecondary">
                  Documents
                </Typography>
                <Box>
                  {(isOwnRep(data?.data.id) || hasPermission(UPLOAD_USER_PAGE_DOCS_PERM)) && (
                    <Button
                      color="primary"
                      variant="contained"
                      startIcon={<UploadIcon />}
                      onClick={() =>
                        push(
                          generatePath(UPLOAD_USER_PAGE_DOCS, {
                            type: 'rep',
                            id: data.data.id,
                          }),
                        )
                      }
                    >
                      Upload documents
                    </Button>
                  )}
                </Box>
              </Box>
              <Box>
                <TableIsoDocuments columns={columnsIsoDocuments} data={data.data.documents} />
              </Box>
            </Paper>
          </Box>
        </>
      )}
      <ModalComponent open={deleteFileState.open}>
        <ConfirmationContent
          text="Are you sure you want delete file?"
          isLoading={isLoadingRemove}
          handleCancel={() =>
            setDeleteFileState({
              open: false,
              id: 0,
            })
          }
          handleConfirm={() => removeDoc(deleteFileState.id)}
        />
      </ModalComponent>
      {data && (
        <ModalComponent open={openManagementFee}>
          <SetManagementRepFeeForm
            onSubmit={(values) => setManagementFeeMut(values)}
            handleCancel={() => setOpenManagementFee(false)}
            initValues={{
              funderId: data.data.id,
              feeValue: data.data.managementFee || '',
            }}
            loading={isLoadingSetManagementFee}
          />
        </ModalComponent>
      )}
    </Box>
  )
}
export default InfoRepContainer
